// Lightweight tracking + UTM + consent helpers (vendor-neutral).
// Non-essential analytics (PostHog) only loads after the visitor explicitly
// grants analytics consent through the CookieBanner.

const STORAGE_KEY = "xa.attribution.v1";
const CONSENT_KEY = "xa.consent.v3";

// Bump this version when categories/wording in CookieBanner change so we
// re-prompt previously-consented visitors.
export const CONSENT_VERSION = 3;

const POSTHOG_KEY = "phc_xAvL2Iq4tFmANRE7kzbKwaSqp1HJjN7x48s3vr0CMjs";
const POSTHOG_HOST = "https://us.i.posthog.com";
let posthogLoaded = false;

function getQuery() {
  if (typeof window === "undefined") return {};
  const p = new URLSearchParams(window.location.search);
  const out = {};
  for (const [k, v] of p.entries()) out[k] = v;
  return out;
}

export function captureAttribution() {
  if (typeof window === "undefined") return null;
  const q = getQuery();
  const utm = {
    utm_source: q.utm_source || null,
    utm_medium: q.utm_medium || null,
    utm_campaign: q.utm_campaign || null,
    utm_term: q.utm_term || null,
    utm_content: q.utm_content || null,
    gclid: q.gclid || null,
    li_click_id: q.li_fat_id || q.li_click_id || null,
    referrer: document.referrer || null,
    landing_page: window.location.pathname + window.location.search,
    captured_at: new Date().toISOString(),
  };
  let stored = null;
  try { stored = JSON.parse(localStorage.getItem(STORAGE_KEY) || "null"); } catch {}
  const merged = stored ? { ...stored, latest: utm } : { original: utm, latest: utm };
  try { localStorage.setItem(STORAGE_KEY, JSON.stringify(merged)); } catch {}
  return merged;
}

export function getAttribution() {
  if (typeof window === "undefined") return { original: {}, latest: {} };
  try {
    return JSON.parse(localStorage.getItem(STORAGE_KEY) || "null") || { original: {}, latest: {} };
  } catch {
    return { original: {}, latest: {} };
  }
}

export function getConsent() {
  if (typeof window === "undefined") return null;
  try {
    const c = JSON.parse(localStorage.getItem(CONSENT_KEY) || "null");
    if (c && c.version === CONSENT_VERSION) return c;
    return null;
  } catch {
    return null;
  }
}

export function setConsent(consent) {
  const payload = {
    version: CONSENT_VERSION,
    necessary: true,
    analytics: !!consent.analytics,
    marketing: !!consent.marketing,
    functional: !!consent.functional,
    ts: new Date().toISOString(),
  };
  try { localStorage.setItem(CONSENT_KEY, JSON.stringify(payload)); } catch {}
  if (typeof window !== "undefined") {
    window.dataLayer = window.dataLayer || [];
    window.dataLayer.push({
      event: "consent_update",
      analytics_storage: payload.analytics ? "granted" : "denied",
      ad_storage: payload.marketing ? "granted" : "denied",
      ad_user_data: payload.marketing ? "granted" : "denied",
      ad_personalization: payload.marketing ? "granted" : "denied",
      functionality_storage: payload.functional ? "granted" : "denied",
      security_storage: "granted",
    });
    applyConsent(payload);
    window.dispatchEvent(new CustomEvent("xa:consent-update", { detail: payload }));
  }
  return payload;
}

/**
 * Apply consent — loads or unloads non-essential analytics.
 * Safe to call multiple times.
 */
export function applyConsent(consent) {
  if (typeof window === "undefined" || !consent) return;
  if (consent.analytics) {
    loadPostHog();
  } else {
    // If PostHog already loaded in a previous session, opt out for this session.
    if (window.posthog && typeof window.posthog.opt_out_capturing === "function") {
      try { window.posthog.opt_out_capturing(); } catch {}
    }
  }
}

/**
 * Inject the PostHog snippet and initialize. Idempotent.
 * Called only after analytics consent.
 */
export function loadPostHog() {
  if (posthogLoaded || typeof window === "undefined") return;
  if (window.posthog && window.posthog.__loaded) return;
  posthogLoaded = true;

  // Minimal stub (same as PostHog official snippet) so calls queue until loaded
  !(function (t, e) {
    e.__SV || ((window.posthog = e), (e._i = []), (e.init = function (i, s, a) {
      function g(t, e) { var o = e.split("."); 2 === o.length && ((t = t[o[0]]), (e = o[1])); t[e] = function () { t.push([e].concat(Array.prototype.slice.call(arguments, 0))); }; }
      var p = t.createElement("script");
      p.type = "text/javascript";
      p.crossOrigin = "anonymous";
      p.async = true;
      p.src = s.api_host.replace(".i.posthog.com", "-assets.i.posthog.com") + "/static/array.js";
      var r = t.getElementsByTagName("script")[0];
      r.parentNode.insertBefore(p, r);
      var u = e;
      void 0 !== a ? (u = e[a] = []) : (a = "posthog");
      u.people = u.people || [];
      u.toString = function (t) { var e = "posthog"; "posthog" !== a && (e += "." + a); return t || (e += " (stub)"), e; };
      u.people.toString = function () { return u.toString(1) + ".people (stub)"; };
      var o = "capture identify alias people.set people.set_once register register_once unregister opt_out_capturing opt_in_capturing reset has_opted_in_capturing has_opted_out_capturing".split(" ");
      for (var n = 0; n < o.length; n++) g(u, o[n]);
      e._i.push([i, s, a]);
      e.__SV = 1;
    }));
  })(document, window.posthog || []);

  window.posthog.init(POSTHOG_KEY, {
    api_host: POSTHOG_HOST,
    person_profiles: "identified_only",
    disable_session_recording: true,
    capture_pageview: true,
    capture_pageleave: true,
    autocapture: false,
    persistence: "localStorage+cookie",
  });
}

export function trackEvent(event, params = {}) {
  if (typeof window === "undefined") return;
  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({ event, ...params, ts: new Date().toISOString() });
  // Mirror to PostHog only when analytics consented + loaded.
  if (window.posthog && window.posthog.__loaded) {
    try { window.posthog.capture(event, params); } catch {}
  }
}

/**
 * Re-open the cookie preferences modal. Wired to the footer
 * "Cookie Preferences" link and any in-page "Manage cookies" affordance.
 */
export function openCookiePreferences() {
  if (typeof window === "undefined") return;
  window.dispatchEvent(new CustomEvent("xa:open-cookie-prefs"));
}
